/**
 * Google Analytics utility functions for tracking events
 * 
 * Events automatically appear in GA4 under Reports > Engagement > Events
 * Custom parameters can be viewed in the event details or by creating custom dimensions
 */

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const GA_MEASUREMENT_ID = 'G-LE117SF8HT';

/**
 * Check if Google Analytics is loaded
 */
export const isAnalyticsLoaded = (): boolean => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Track a page view
 */
export const trackPageView = (path: string, title?: string): void => {
  if (!isAnalyticsLoaded()) return;

  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: path,
    page_title: title,
  });
};

/**
 * Track a custom event
 */
export const trackEvent = (
  eventName: string,
  eventParams?: {
    [key: string]: string | number | boolean | null | undefined;
  }
): void => {
  if (!isAnalyticsLoaded()) return;

  window.gtag('event', eventName, eventParams);
};

/**
 * Common event tracking helpers
 * 
 * Event naming follows GA4 conventions:
 * - Use snake_case for event names
 * - Use descriptive, action-based names
 * - Include relevant context as parameters
 */
export const analytics = {
  // ============================================
  // USER AUTHENTICATION
  // ============================================
  login: (method?: string) => 
    trackEvent('login', { method: method || 'email' }),
  
  logout: () => 
    trackEvent('logout'),
  
  signup: (method?: string) => 
    trackEvent('sign_up', { method: method || 'email' }),

  // ============================================
  // GUIDE ACTIONS
  // ============================================
  guideCreated: (guideId: string, destination?: string) =>
    trackEvent('guide_created', { 
      guide_id: guideId,
      destination: destination,
    }),
  
  guidePublished: (guideId: string, locale?: string) =>
    trackEvent('guide_published', { 
      guide_id: guideId,
      locale: locale,
    }),
  
  guideUnpublished: (guideId: string) =>
    trackEvent('guide_unpublished', { guide_id: guideId }),
  
  guideSaved: (guideId: string) =>
    trackEvent('guide_saved', { guide_id: guideId }),
  
  guideDeleted: (guideId: string) =>
    trackEvent('guide_deleted', { guide_id: guideId }),
  
  guideViewed: (guideId: string, viewType: 'preview' | 'live') =>
    trackEvent('guide_viewed', { 
      guide_id: guideId,
      view_type: viewType,
    }),
  
  // Legacy aliases for backward compatibility
  createGuide: (guideId?: string) =>
    trackEvent('create_guide', guideId ? { guide_id: guideId } : undefined),
  editGuide: (guideId?: string) =>
    trackEvent('edit_guide', guideId ? { guide_id: guideId } : undefined),
  deleteGuide: (guideId?: string) =>
    trackEvent('delete_guide', guideId ? { guide_id: guideId } : undefined),
  publishGuide: (guideId?: string) =>
    trackEvent('publish_guide', guideId ? { guide_id: guideId } : undefined),

  // ============================================
  // CAMPAIGN ACTIONS
  // ============================================
  campaignViewed: (campaignId: string, campaignName?: string) =>
    trackEvent('campaign_viewed', { 
      campaign_id: campaignId,
      campaign_name: campaignName,
    }),
  
  externalOpportunityClicked: (campaignId: string, campaignName?: string) =>
    trackEvent('external_opportunity_clicked', {
      campaign_id: campaignId,
      campaign_name: campaignName,
    }),

  campaignApplicationStarted: (campaignId: string) =>
    trackEvent('campaign_application_started', { campaign_id: campaignId }),
  
  campaignApplicationSubmitted: (campaignId: string, campaignName?: string) =>
    trackEvent('campaign_application_submitted', { 
      campaign_id: campaignId,
      campaign_name: campaignName,
    }),

  // ============================================
  // MEDIA ACTIONS
  // ============================================
  mediaUploaded: (mediaType: 'image' | 'video', source?: string) =>
    trackEvent('media_uploaded', { 
      media_type: mediaType,
      source: source, // 'local', 'instagram', 'tiktok', etc.
    }),
  
  mediaImportStarted: (platform: string) =>
    trackEvent('media_import_started', { platform }),
  
  mediaImportCompleted: (platform: string, count: number) =>
    trackEvent('media_import_completed', { 
      platform,
      media_count: count,
    }),
  
  mediaDeleted: (mediaId?: string) =>
    trackEvent('media_deleted', mediaId ? { media_id: mediaId } : undefined),
  
  // Legacy alias
  uploadMedia: (mediaType?: string) =>
    trackEvent('upload_media', mediaType ? { media_type: mediaType } : undefined),
  deleteMedia: (mediaId?: string) =>
    trackEvent('delete_media', mediaId ? { media_id: mediaId } : undefined),

  // ============================================
  // PROFILE ACTIONS
  // ============================================
  profileUpdated: (fieldsUpdated?: string[]) =>
    trackEvent('profile_updated', { 
      fields_updated: fieldsUpdated?.join(','),
    }),
  
  socialAccountConnected: (platform: string) =>
    trackEvent('social_account_connected', { platform }),
  
  socialAccountDisconnected: (platform: string) =>
    trackEvent('social_account_disconnected', { platform }),

  // ============================================
  // ONBOARDING
  // ============================================
  onboardingStepViewed: (step: string | number) =>
    trackEvent('onboarding_step_viewed', { step: String(step) }),
  
  onboardingCompleted: () =>
    trackEvent('onboarding_completed'),

  // ============================================
  // ENGAGEMENT
  // ============================================
  featureUsed: (featureName: string, details?: Record<string, string | number | boolean>) =>
    trackEvent('feature_used', { 
      feature_name: featureName,
      ...details,
    }),
  
  errorOccurred: (errorType: string, errorMessage?: string) =>
    trackEvent('error_occurred', { 
      error_type: errorType,
      error_message: errorMessage,
    }),

  // ============================================
  // GENERIC HELPERS
  // ============================================
  pageView: trackPageView,
  customEvent: trackEvent,
};

